<script setup>
import DeleteAction from '@js/components/actions/DeleteAction.vue';
import ActionButton from '@js/components/buttons/ActionButton.vue';
import DialogDrawer from '@js/components/dialogs/DialogDrawer.vue';
import CodeField from '@js/components/fields/CodeField.vue';
import EditorField from '@js/components/fields/EditorField.vue';
import InputField from '@js/components/fields/InputField.vue';
import ListboxField from '@js/components/fields/ListboxField.vue';
import SwitchField from '@js/components/fields/SwitchField.vue';
import FormColumn from '@js/components/forms/FormColumn.vue';
import FormContent from '@js/components/forms/FormContent.vue';
import FormFooter from '@js/components/forms/FormFooter.vue';
import FormGrid from '@js/components/forms/FormGrid.vue';
import FormRoot from '@js/components/forms/FormRoot.vue';
import { useRoute } from '@js/utils/route';
import { toastError, toastSaved } from '@js/utils/toast';
import { useChangeCase } from '@vueuse/integrations/useChangeCase';
import { reactive, ref, watch } from 'vue';
import { boolean, object, string } from 'yup';

const { tenant, emailing, emailingVariable, callback } = defineProps({
    tenant: {
        required: true,
        type: Object,
    },
    emailing: {
        required: false,
        type: Object,
    },
    emailingVariable: {
        required: false,
        type: Object,
    },
    callback: {
        type: Function,
        required: false,
    },
});

const { route } = useRoute();

const isOpen = ref(false);

const isSubmitting = ref(false);

const loading = ref(false);

const emailingVariableTypes = ref([]);

const form = reactive({
    name: emailingVariable ? emailingVariable.name : '',
    type: null,
    rawValue: emailingVariable ? emailingVariable.rawValue : '',
    isActive: emailingVariable ? emailingVariable.isActive : true,
});

const validationSchema = object({
    name: string().required('Le nom est requis'),
    type: object().required('Le type est requis'),
    rawValue: string().required('La valeur est requise'),
    isActive: boolean().required("L'état actif est requis"),
});

function updateData() {
    loading.value = true;

    axios
        .get(route('front.projectManagement.modules.getEmailingVariableTypes'))
        .then((response) => {
            emailingVariableTypes.value = response.data;

            form.type = emailingVariable
                ? emailingVariableTypes.value.find(
                      (type) => type.value === emailingVariable.type.value,
                  )
                : emailingVariableTypes.value[0];
        })
        .finally(() => {
            loading.value = false;
        })
        .catch((error) => {
            toastError(error);
        });
}

watch(isOpen, (value) => {
    if (value) {
        updateData();
    }
});

function submit() {
    isSubmitting.value = true;

    axios
        .post(
            route('front.projectManagement.modules.postEmailingVariable', {
                tenant: tenant.id,
                ...(emailingVariable
                    ? {
                          emailingVariable: emailingVariable.id,
                      }
                    : {}),
            }),
            {
                name: useChangeCase(form.name, 'snakeCase').value,
                type: form.type.value,
                rawValue: form.rawValue,
                isActive: form.isActive,
                ...(emailing ? { emailingId: emailing.id } : {}),
            },
        )
        .then(() => {
            toastSaved();

            isOpen.value = false;

            if (callback) {
                callback();
            }
        })
        .finally(() => {
            isSubmitting.value = false;
        })
        .catch((error) => {
            toastError(error);
        });
}
</script>

<template>
    <DialogDrawer v-model="isOpen" size="almost-full" :loading="loading">
        <template #trigger>
            <slot name="trigger" />
        </template>

        <template #title>
            <template v-if="emailingVariable">
                Modifier la variable d'emailing
            </template>

            <template v-else> Créer une variable d'emailing </template>
        </template>

        <template #full-content>
            <FormRoot
                v-slot="{ errors }"
                :validation-schema="validationSchema"
                :submit="submit"
            >
                <FormColumn>
                    <FormContent>
                        <SwitchField
                            v-model="form.isActive"
                            label="Est actif"
                            field-name="isActive"
                            field-id="isActive"
                        />
                    </FormContent>

                    <FormContent>
                        <FormGrid :columns="2">
                            <ListboxField
                                v-model="form.type"
                                field-id="type"
                                field-name="type"
                                label="Type"
                                :options="emailingVariableTypes"
                                by="value"
                                required
                            >
                                <template #buttonContent>
                                    {{ form.type.label }}
                                </template>

                                <template #optionContent="{ option }">
                                    {{ option.label }}
                                </template>
                            </ListboxField>

                            <InputField
                                v-model="form.name"
                                label="Nom"
                                field-name="name"
                                field-id="name"
                                required
                            />
                        </FormGrid>
                    </FormContent>

                    <FormContent>
                        <CodeField
                            v-if="form.type.value === 'html'"
                            v-model="form.rawValue"
                            field-name="rawValue"
                            field-id="rawValue"
                            label="Valeur"
                            size="small"
                        />

                        <EditorField
                            v-if="form.type.value === 'editor'"
                            v-model="form.rawValue"
                            label="Valeur"
                            field-name="rawValue"
                            field-id="rawValue"
                            required
                            size="small"
                        />

                        <InputField
                            v-if="form.type.value === 'string'"
                            v-model="form.rawValue"
                            label="Valeur"
                            field-name="rawValue"
                            field-id="rawValue"
                            type="text"
                            required
                        />

                        <InputField
                            v-if="form.type.value === 'integer'"
                            v-model="form.rawValue"
                            label="Valeur"
                            field-name="rawValue"
                            field-id="rawValue"
                            type="number"
                            step="1"
                            required
                        />

                        <InputField
                            v-if="form.type.value === 'float'"
                            v-model="form.rawValue"
                            label="Valeur"
                            field-name="rawValue"
                            field-id="rawValue"
                            type="number"
                            step="0.01"
                            required
                        />

                        <SwitchField
                            v-if="form.type.value === 'boolean'"
                            v-model="form.rawValue"
                            field-name="rawValue"
                            field-id="rawValue"
                            label="Valeur"
                        />

                        <InputField
                            v-if="form.type.value === 'date'"
                            v-model="form.rawValue"
                            label="Valeur"
                            field-name="rawValue"
                            field-id="rawValue"
                            type="date"
                            required
                        />
                    </FormContent>
                </FormColumn>

                <FormFooter
                    :is-submitting="isSubmitting"
                    :errors="errors"
                    without-back-button
                >
                    <template #actions>
                        <DeleteAction
                            v-if="emailingVariable"
                            :url="
                                route(
                                    'front.projectManagement.modules.deleteEmailingVariable',
                                    {
                                        tenant: tenant.id,
                                        emailingVariable: emailingVariable.id,
                                    },
                                )
                            "
                            :callback="
                                () => {
                                    isOpen = false;

                                    if (callback) {
                                        callback();
                                    }
                                }
                            "
                        >
                            <template #trigger>
                                <ActionButton type="secondary">
                                    Supprimer
                                </ActionButton>
                            </template>
                        </DeleteAction>
                    </template>
                </FormFooter>
            </FormRoot>
        </template>
    </DialogDrawer>
</template>
